import type { ReactNode } from 'react';
import { createContext, useContext, useState, useCallback } from 'react';
import { Spinner } from './Spinner';

import styles from './Spinner.module.css';

interface SpinnerContextValue {
  isLoading: boolean;
  show: () => void;
  hide: () => void;
}

const SpinnerContext = createContext<SpinnerContextValue | null>(null);

export interface SpinnerProviderProps {
  children: ReactNode;
}

export function SpinnerProvider({ children }: SpinnerProviderProps) {
  const [activeRequests, setActiveRequests] = useState(0);

  const show = useCallback(() => {
    setActiveRequests((prev) => prev + 1);
  }, []);

  const hide = useCallback(() => {
    setActiveRequests((prev) => Math.max(0, prev - 1));
  }, []);

  const isLoading = activeRequests > 0;

  const value: SpinnerContextValue = {
    isLoading,
    show,
    hide,
  };

  return (
    <SpinnerContext.Provider value={value}>
      {children}
      {isLoading && (
        <div className={styles.overlay}>
          <Spinner size="lg" />
        </div>
      )}
    </SpinnerContext.Provider>
  );
}

export function useSpinnerContext(): SpinnerContextValue {
  const context = useContext(SpinnerContext);
  if (!context) {
    throw new Error('useSpinnerContext must be used within a SpinnerProvider');
  }
  return context;
}
